/**
 * Required-field enforcement for Firestore documents.
 *
 * Firestore is configured with `ignoreUndefinedProperties: true`, so writing a
 * document with a required field set to `undefined` is not an error — the field
 * is silently dropped. The write looks like it succeeded. The failure surfaces
 * much later, on the read path, in whichever component first trusts the
 * declared type — and because documents are read through unvalidated
 * `{ id, ...data } as Trial` casts, that component throws rather than degrades.
 *
 * These helpers move the failure back to the write, where whoever caused it can
 * see it. Use them in every canonical document constructor (app services and
 * admin scripts alike) rather than hand-assembling a document literal.
 *
 * The field lists are derived from the TypeScript interfaces via
 * `requiredFieldsOf`, so they cannot drift: adding a required property to an
 * interface fails the build until the list is updated.
 *
 * That guarantee is only as good as the gate that runs it. `vite build` and
 * Vitest transpile without typechecking, so it is enforced by
 * `npm run lint:domain-types` (`tsconfig.domain.json`), which CI runs.
 * **A new `*_REQUIRED_FIELDS` list must be added to that project's `include`,
 * or it is not gated.**
 */

/**
 * The keys of `T` that are NOT declared optional (`?`).
 *
 * This works under the repo's `strictNullChecks: false` setting because `?`
 * optionality is tracked independently of null-strictness — `string | null` is
 * required, `string | undefined` via `?` is not.
 */
export type RequiredKeys<T> = {
  // `{}` is load-bearing here: `{} extends Pick<T, K>` is only true when K is
  // optional, which is the whole test. `object`/`unknown` do not work.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  [K in keyof T]-?: {} extends Pick<T, K> ? never : K;
}[keyof T];

/**
 * Builds a required-field list that the compiler checks against `T` in both
 * directions: every entry must be a required key, and every required key must
 * be listed.
 *
 * Omitting one produces an error naming it in `__missingRequiredFields`.
 *
 * @example
 * export const TRIAL_REQUIRED_FIELDS = requiredFieldsOf<Trial>()([
 *   "name", "orgId", "startDate", // ...
 * ] as const);
 */
export function requiredFieldsOf<T>() {
  return <L extends readonly RequiredKeys<T>[]>(
    fields: L &
      ([RequiredKeys<T>] extends [L[number]]
        ? unknown
        : { __missingRequiredFields: Exclude<RequiredKeys<T>, L[number]> })
  ): L => fields;
}

/**
 * Which of `fields` the document does not supply.
 *
 * `null` counts as supplied — it is a stored value, and several required fields
 * (`description`, `endDate`, `crops`) are legitimately null. Only `undefined`
 * and absent keys count as missing, because those are the two that Firestore
 * collapses into the same stored document.
 */
export const missingRequiredFields = (doc: unknown, fields: readonly string[]): string[] => {
  if (doc === null || typeof doc !== "object") return [...fields];
  const record = doc as Record<string, unknown>;
  return fields.filter((field) => record[field] === undefined);
};

/**
 * Returns `doc` unchanged, or throws naming every missing field.
 *
 * Deliberately reports all of them at once: the staging trial that white-screened
 * a whole organization's list was missing six, and fixing them one round-trip at
 * a time is how you conclude the data is fine after fixing the first.
 */
export const assertCompleteDocument = <T>(
  doc: T,
  fields: readonly string[],
  documentKind: string
): T => {
  const missing = missingRequiredFields(doc, fields);
  if (missing.length > 0) {
    throw new Error(
      `Refusing to write an incomplete ${documentKind} document: missing required ` +
        `field(s) ${missing.sort().join(", ")}. Firestore runs with ` +
        `ignoreUndefinedProperties, so this would be stored silently and fail later on read.`
    );
  }
  return doc;
};
